/* Bonvent supply notices. Consent is collected before any cart/add request. */
(() => {
  'use strict';
  if (window.BonventSupplyDelay) return;
  const source = document.getElementById('bv-supply-delays');
  if (!source) return;
  let data;
  try {
    data = JSON.parse(source.textContent);
    if (data.rulesEncoded) data.rules = data.rulesEncoded.map(value => JSON.parse(new TextDecoder().decode(Uint8Array.from(atob(value), char => char.charCodeAt(0)))));
  } catch { return; }
  const rules = (data.rules || []).filter(rule => rule.enabled && rule.delay && rule.message);
  if (!rules.length) return;
  const language = (document.documentElement.lang || 'en').replace(/_/g,'-').toLowerCase();
  const baseLanguage = language.split('-')[0], fr = baseLanguage === 'fr';
  function translation(rule) {
    const values = rule.translations || {};
    return values[language] || values[baseLanguage] || (language === baseLanguage ? Object.entries(values).find(([key]) => key.split('-')[0] === baseLanguage)?.[1] : undefined);
  }
  const fallbackCopy = fr ? {
    title: 'Un délai d’approvisionnement est à prévoir', notice: 'Avant votre commande',
    confirm: 'J’accepte ce délai et j’ajoute au panier', cancel: 'Annuler', close: 'Continuer ma visite',
    check: 'J’ai lu et j’accepte le délai annoncé pour les produits ci-dessus.',
    property: 'Délai d’approvisionnement accepté', details: 'Voir le délai',
    cancelled: 'Ajout annulé. Aucun produit n’a été ajouté.',
  } : {
    title: 'Please allow for a supply lead time', notice: 'Before you order',
    confirm: 'I accept the lead time — add to cart', cancel: 'Cancel', close: 'Continue browsing',
    check: 'I have read and accept the stated lead time for the products above.',
    property: 'Supply lead time accepted', details: 'View lead time',
    cancelled: 'Cancelled. No products were added.',
  };
  const translatedCopy = !['fr','en'].includes(baseLanguage) && rules.map(translation).find(value => value?.copy)?.copy;
  const copy = {...fallbackCopy,...translatedCopy};
  const propertyKeys = new Set(['Supply lead time accepted','Délai d’approvisionnement accepté',...rules.flatMap(rule => Object.values(rule.translations || {}).map(value => value.copy?.property).filter(Boolean))]);
  const byVariant = new Map(), byProduct = new Map();
  const id = value => String(value || '').split('/').pop();
  for (const rule of rules) {
    byProduct.set(id(rule.productId), rule);
    for (const variant of rule.variantIds || []) byVariant.set(id(variant), rule);
  }
  const currentRule = byProduct.get(id(data.productId));
  if (currentRule) for (const variant of data.currentVariants || []) byVariant.set(id(variant),currentRule);
  function wording(rule) {
    const localized = !['fr','en'].includes(baseLanguage) && translation(rule);
    if (localized?.delay && localized?.message) return localized;
    return !fr && rule.english?.delay && rule.english?.message ? rule.english : rule;
  }
  function element(tag, className, text) {
    const el = document.createElement(tag); if (className) el.className = className;
    if (text !== undefined) el.textContent = text; return el;
  }
  let queue = Promise.resolve();
  function ask(affected, confirmation = true) {
    const run = () => new Promise(resolve => {
      const previous = document.activeElement;
      const dialog = element('dialog', 'bv-delay-dialog');
      const heading = element('h2', '', copy.title); heading.id = 'bv-delay-title';
      dialog.setAttribute('aria-labelledby', heading.id);
      dialog.append(element('p', 'bv-delay-eyebrow', copy.notice), heading);
      const content = element('div', 'bv-delay-products');
      for (const rule of affected) {
        const text = wording(rule), row = element('section', 'bv-delay-product');
        row.append(element('h3', '', rule.productTitle), element('p', 'bv-delay-duration', text.delay), element('p', 'bv-delay-message', text.message));
        content.append(row);
      }
      dialog.append(content);
      const actions = element('div', 'bv-delay-actions');
      const cancel = element('button', 'bv-delay-secondary', confirmation ? copy.cancel : copy.close); cancel.type = 'button';
      let finished = false;
      const finish = accepted => {
        if (finished) return; finished = true;
        dialog.close(); dialog.remove();
        if (previous?.isConnected) previous.focus({preventScroll:true});
        resolve(accepted);
      };
      cancel.addEventListener('click', () => finish(false));
      if (confirmation) {
        const label = element('label', 'bv-delay-agreement');
        const check = element('input'); check.type = 'checkbox';
        label.append(check, element('span', '', copy.check)); dialog.append(label);
        const accept = element('button', 'bv-delay-primary', copy.confirm); accept.type = 'button'; accept.disabled = true;
        check.addEventListener('change', () => { accept.disabled = !check.checked; });
        accept.addEventListener('click', () => { if (check.checked) finish(true); });
        actions.append(accept);
      }
      actions.append(cancel); dialog.append(actions);
      dialog.addEventListener('cancel', event => { event.preventDefault(); finish(false); });
      // Keep Escape local: an Option Builder may itself be open underneath.
      dialog.addEventListener('keydown', event => { if (event.key === 'Escape') event.stopPropagation(); });
      dialog.addEventListener('click', event => {
        if (event.target !== dialog) return;
        const box = dialog.getBoundingClientRect();
        if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) finish(false);
      });
      document.body.append(dialog); dialog.showModal(); cancel.focus();
    });
    const next = queue.then(run); queue = next.catch(() => {}); return next;
  }
  function ruleFor(item) { return byProduct.get(id(item.productId)) || byVariant.get(id(item.id)); }
  function consentProperties(rule) {
    const text = wording(rule);
    return {
      [copy.property]: text.delay,
      _bv_supply_delay: JSON.stringify({version:rule.version,productId:rule.productId,locale:language,delay:text.delay,message:text.message,acceptedAt:new Date().toISOString()}),
    };
  }
  function cancelled() { const error = new Error(copy.cancelled); error.code = 'BV_DELAY_CANCELLED'; return error; }
  async function prepare(items) {
    const affected = [...new Map(items.filter(item => Number(item.quantity ?? 1) > 0).map(item => ruleFor(item)).filter(Boolean).map(rule => [rule.productId,rule])).values()];
    if (!affected.length) return items;
    if (!await ask(affected)) throw cancelled();
    return items.map(item => {
      const rule = ruleFor(item); if (!rule || Number(item.quantity ?? 1) <= 0) return item;
      const properties = {...item.properties};
      for (const key of propertyKeys) delete properties[key];
      return {...item,properties:{...properties,...consentProperties(rule)}};
    });
  }
  function parameterItems(params) {
    if (params.has('id')) return [{id:params.get('id'),quantity:params.get('quantity')||1,productId:params.get('product-id'),prefix:''}];
    const items = [];
    for (const key of params.keys()) {
      const match = /^items\[(\d+)\]\[id\]$/.exec(key);
      if (match) items.push({id:params.get(key),quantity:params.get(`items[${match[1]}][quantity]`)||1,prefix:`items[${match[1]}]`});
    }
    return items;
  }
  async function prepareParameters(params) {
    const items = parameterItems(params), prepared = await prepare(items);
    for (const item of prepared) {
      if (!item.properties) continue;
      const name = key => item.prefix ? `${item.prefix}[properties][${key}]` : `properties[${key}]`;
      for (const key of propertyKeys) params.delete(name(key));
      for (const [key,value] of Object.entries(item.properties)) params.set(name(key),value);
    }
    return params;
  }
  const nativeFetch = window.fetch;
  window.fetch = async function(input, init) {
    const isRequest = input instanceof Request;
    const url = new URL(isRequest ? input.url : String(input), location.href);
    const method = (init?.method || (isRequest ? input.method : 'GET')).toUpperCase();
    if (url.origin !== location.origin || method !== 'POST' || !/\/cart\/add(?:\.js|\.json)?\/?$/.test(url.pathname)) return nativeFetch.call(this,input,init);
    let body = init?.body, headers = new Headers(init?.headers || (isRequest ? input.headers : undefined));
    if (body === undefined && isRequest) {
      const request = input.clone(), type = headers.get('Content-Type') || '';
      body = type.includes('multipart/form-data') ? await request.formData() : await request.text();
    }
    if (body instanceof FormData) {
      const cloned = new FormData(); for (const [key,value] of body) cloned.append(key,value);
      body = await prepareParameters(cloned); headers.delete('Content-Type');
    } else if (body instanceof URLSearchParams) {
      body = await prepareParameters(new URLSearchParams(body));
    } else if (typeof body === 'string') {
      const type = headers.get('Content-Type') || '';
      if (type.includes('json') || body.trim().startsWith('{')) {
        const payload = JSON.parse(body), items = payload.items || [payload];
        const prepared = await prepare(items);
        body = JSON.stringify(payload.items ? {...payload,items:prepared} : prepared[0]);
      } else {
        body = (await prepareParameters(new URLSearchParams(body))).toString();
      }
    }
    return nativeFetch.call(this,input,{...init,body,headers});
  };
  const allowed = new WeakSet(), pending = new WeakSet();
  document.addEventListener('submit', async event => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !/\/cart\/add(?:\.js)?\/?$/.test(new URL(form.action,location.href).pathname)) return;
    if (allowed.has(form)) { allowed.delete(form); return; }
    // These paths submit via fetch; gate the complete bundle (including its accessories) there.
    if (form.closest('product-form') || form.querySelector('.bv-options-builder')) return;
    const values = new FormData(form), items = parameterItems(values);
    if (!items.some(ruleFor)) return;
    event.preventDefault(); event.stopImmediatePropagation();
    if (pending.has(form)) return; pending.add(form);
    try {
      const before = JSON.stringify(items), prepared = await prepare(items);
      if (before !== JSON.stringify(parameterItems(new FormData(form)))) return;
      for (const input of form.querySelectorAll('[data-bv-delay-property]')) input.remove();
      for (const [key,value] of Object.entries(prepared[0].properties || {})) {
        const input = element('input'); input.type = 'hidden'; input.name = `properties[${key}]`; input.value = value; input.dataset.bvDelayProperty = ''; form.append(input);
      }
      allowed.add(form); form.requestSubmit(event.submitter || undefined);
    } catch (error) { if (error.code !== 'BV_DELAY_CANCELLED') console.error('Bonvent supply notice:',error); }
    finally { pending.delete(form); }
  }, true);
  function mountNotice() {
    const rule = byProduct.get(id(data.productId)); if (!rule) return;
    const forms = [...document.querySelectorAll('form[action*="/cart/add"]')].filter(form => form.querySelector('button[type="submit"],button[name="add"],button:not([type]),input[type="submit"]'));
    const form = forms.find(form => {
      const values = new FormData(form);
      return id(values.get('product-id')) === id(data.productId) || byVariant.get(id(values.get('id'))) === rule;
    }) || forms.find(form => form.matches('.bvm-zero-form') || form.closest('product-info'));
    if (!form || form.querySelector('.bv-delay-banner')) return;
    const banner = element('div','bv-delay-banner');
    const text = wording(rule), button = element('button','bv-delay-link',copy.details); button.type = 'button';
    button.addEventListener('click',()=>ask([rule],false));
    banner.append(element('strong','',copy.title),element('span','',text.delay),button); form.prepend(banner);
    if (rule.showOnPage) ask([rule],false);
  }
  window.BonventSupplyDelay = {prepare};
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded',mountNotice,{once:true}); else mountNotice();
})();
